import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './CommonStyles.css';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import apiService from '../../services/api';
import { toast } from 'react-toastify';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute] = match;
      let h = parseInt(hour, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      h = h ? h : 12;
      return `${month}/${day}/${year} ${String(h).padStart(2, '0')}:${minute} ${ampm}`;
    }
    return dateStr;
  } catch (e) {
    return dateStr;
  }
};

const PaymentAccounts = ({ activeTab: propActiveTab }) => {
  const { formatCurrency, formatDate, formatDateTime, quantityPrecision } = useBusinessSettings();
  const [activeTab, setActiveTab] = useState('list_accounts');

  // Synchronize state with prop changes from dashboard popover
  useEffect(() => {
    if (propActiveTab) {
      if (propActiveTab === 'payment-accounts' || propActiveTab === 'payment_accounts') {
        setActiveTab('list_accounts');
      } else {
        setActiveTab(propActiveTab);
      }
    }
  }, [propActiveTab]);

  // ─── Dedicated Balance Sheet Print Function ───
  // Opens a clean new window with just the balance sheet HTML.
  // This bypasses ALL CSS layout/overflow issues with window.print().
  const printBalanceSheet = (data, dateStr) => {
    if (!data) return;
    const title = document.querySelector('title')?.textContent || 'Balance Sheet';
    const dateLabel = new Date(dateStr || new Date()).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const fmt = (val) => {
      const num = parseFloat(val) || 0;
      return (num < 0 ? '$ -' : '$ ') + Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const totalAssets = (data.customer_due || 0)
      + (data.closing_stock || 0)
      + Object.values(data.account_balances || {}).reduce((a, b) => a + parseFloat(b || 0), 0);

    const accountRows = Object.entries(data.account_balances || {})
      .map(([name, bal]) => `<tr><td style="padding:4px 14px 4px 24px;color:#475569;">${name}</td><td style="padding:4px 14px;text-align:right;font-family:monospace;">${fmt(bal)}</td></tr>`)
      .join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:Arial,sans-serif;font-size:13px;color:#1e293b;padding:28px;}
    h1{font-size:17px;font-weight:700;margin-bottom:3px;}
    .sub{font-size:12px;color:#64748b;margin-bottom:20px;}
    .grid{display:grid;grid-template-columns:1fr 1fr;border:1px solid #cbd5e1;border-radius:6px;overflow:hidden;}
    .col{display:flex;flex-direction:column;}
    .col-l{border-right:1px solid #cbd5e1;}
    .col-head{background:#f1f5f9;padding:9px 14px;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.05em;border-bottom:1px solid #cbd5e1;}
    table{width:100%;border-collapse:collapse;}
    .row td{border-bottom:1px dashed #e2e8f0;padding:7px 14px;}
    .row td:last-child{text-align:right;font-family:monospace;}
    .section{padding:8px 14px 3px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#64748b;}
    .total{background:#f1f5f9;border-top:2px solid #cbd5e1;}
    .total td{padding:10px 14px;font-weight:700;}
    .total td:last-child{text-align:right;font-family:monospace;}
    @media print{body{padding:12px;}}
  </style>
</head>
<body>
  <h1>${title}</h1>
  <p class="sub">As of ${dateLabel} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()}</p>
  <div class="grid">
    <div class="col col-l">
      <div class="col-head">Liability</div>
      <table>
        <tr class="row"><td>Supplier Due:</td><td>${fmt(data.supplier_due || 0)}</td></tr>
      </table>
      <table><tr class="total"><td>Total Liability:</td><td>${fmt(data.supplier_due || 0)}</td></tr></table>
    </div>
    <div class="col">
      <div class="col-head">Assets</div>
      <table>
        <tr class="row"><td>Customer Due:</td><td>${fmt(data.customer_due || 0)}</td></tr>
        <tr class="row"><td>Closing Stock:</td><td>${fmt(data.closing_stock || 0)}</td></tr>
        <tr><td colspan="2" class="section">Account Balances:</td></tr>
        ${accountRows}
      </table>
      <table><tr class="total"><td>Total Assets:</td><td>${fmt(totalAssets)}</td></tr></table>
    </div>
  </div>
  <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=900,height=650');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ─── Print: List Accounts ───
  const printListAccounts = (data) => {
    const title = 'List of Accounts';
    const rows = data.map(a => `
      <tr>
        <td>${a.name || '-'}${a.is_closed == 1 ? ' <span style="background:#dc2626;color:#fff;padding:1px 5px;border-radius:3px;font-size:10px;margin-left:4px;">Closed</span>' : ''}</td>
        <td>${a.parent_account_type_name || a.account_type_name || '-'}</td>
        <td>${a.account_type_name || '-'}</td>
        <td>${a.account_number || '-'}</td>
        <td>${a.note || '-'}</td>
        <td style="text-align:right;font-family:monospace;color:${(a.balance || 0) >= 0 ? '#10b981' : '#ef4444'};">${(a.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${a.added_by || '-'}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;padding:24px;}h1{font-size:16px;font-weight:700;margin-bottom:4px;}.sub{font-size:11px;color:#64748b;margin-bottom:16px;}table{width:100%;border-collapse:collapse;}th{background:#f1f5f9;padding:8px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:11px;text-transform:uppercase;letter-spacing:.04em;}td{padding:7px 8px;border-bottom:1px solid #e2e8f0;vertical-align:middle;}@media print{body{padding:10px;}}</style>
</head><body>
<h1>${title}</h1><p class="sub">Generated: ${new Date().toLocaleString()}</p>
<table><thead><tr><th>Name</th><th>Account Type</th><th>Account Sub Type</th><th>Account Number</th><th>Note</th><th style="text-align:right;">Balance</th><th>Added By</th></tr></thead>
<tbody>${rows}</tbody></table>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
</body></html>`;
    const w = window.open('', '_blank', 'width=1100,height=700');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ─── Print: Account Types ───
  const printAccountTypes = (data) => {
    const title = 'Account Types';
    const rows = data.map(t => `<tr><td>${t.name || '-'}</td><td>${t.parent_name || '-'}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;padding:24px;}h1{font-size:16px;font-weight:700;margin-bottom:4px;}.sub{font-size:11px;color:#64748b;margin-bottom:16px;}table{width:100%;border-collapse:collapse;}th{background:#f1f5f9;padding:8px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:11px;text-transform:uppercase;letter-spacing:.04em;}td{padding:7px 8px;border-bottom:1px solid #e2e8f0;}@media print{body{padding:10px;}}</style>
</head><body>
<h1>${title}</h1><p class="sub">Generated: ${new Date().toLocaleString()}</p>
<table><thead><tr><th>Account Type</th><th>Parent Account Type</th></tr></thead>
<tbody>${rows}</tbody></table>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
</body></html>`;
    const w = window.open('', '_blank', 'width=700,height=600');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ─── Print: Trial Balance ───
  const printTrialBalance = (data, dateStr) => {
    if (!data) return;
    const fmt = (val) => {
      const num = parseFloat(val) || 0;
      return (num < 0 ? '-' : '') + Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const dateLabel = new Date(dateStr || new Date()).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const debitTotal = (data.customer_due || 0) + Object.values(data.account_balances || {}).reduce((a, b) => a + parseFloat(b || 0), 0);
    const accountRows = Object.entries(data.account_balances || {})
      .map(([name, bal]) => `<tr><td style="padding-left:24px;">${name}:</td><td style="text-align:right;font-family:monospace;">${fmt(bal)}</td><td>&nbsp;</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Trial Balance</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;padding:24px;}h1{font-size:16px;font-weight:700;margin-bottom:4px;}.sub{font-size:11px;color:#64748b;margin-bottom:16px;}table{width:100%;border-collapse:collapse;}th{background:#f1f5f9;padding:8px;text-align:right;border-bottom:2px solid #cbd5e1;font-size:11px;text-transform:uppercase;letter-spacing:.04em;}th:first-child{text-align:left;}td{padding:6px 8px;border-bottom:1px solid #e2e8f0;}.total td{background:#f1f5f9;font-weight:700;border-top:2px solid #cbd5e1;border-bottom:none;}@media print{body{padding:10px;}}</style>
</head><body>
<h1>Trial Balance</h1><p class="sub">As of ${dateLabel} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()}</p>
<table><thead><tr><th style="text-align:left;">Trial Balance</th><th>Debit</th><th>Credit</th></tr></thead>
<tbody>
<tr><td style="font-weight:600;">Supplier Due:</td><td>&nbsp;</td><td style="text-align:right;font-family:monospace;">${fmt(data.supplier_due || 0)}</td></tr>
<tr><td style="font-weight:600;">Customer Due:</td><td style="text-align:right;font-family:monospace;">${fmt(data.customer_due || 0)}</td><td>&nbsp;</td></tr>
<tr><td style="font-weight:600;">Account Balances:</td><td>&nbsp;</td><td>&nbsp;</td></tr>
${accountRows}
</tbody>
<tfoot><tr class="total"><td>Total</td><td style="text-align:right;font-family:monospace;">${fmt(debitTotal)}</td><td style="text-align:right;font-family:monospace;">${fmt(data.supplier_due || 0)}</td></tr></tfoot>
</table>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
</body></html>`;
    const w = window.open('', '_blank', 'width=800,height=650');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ─── Print: Cash Flow ───
  const printCashFlow = (data, startDate, endDate) => {
    const fmt = (val) => {
      const num = parseFloat(val) || 0;
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const fmtDate = (d) => {
      if (!d) return '-';
      const match = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return match ? `${match[2]}/${match[3]}/${match[1]}` : d;
    };
    const rows = data.map(r => `<tr>
      <td>${fmtDate(r.date)}</td>
      <td>${r.account_name || '-'}</td>
      <td>${(r.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}</td>
      <td>${r.method || '-'}</td>
      <td style="text-align:right;font-family:monospace;color:#ef4444;">${r.type === 'debit' ? fmt(r.amount || 0) : ''}</td>
      <td style="text-align:right;font-family:monospace;color:#10b981;">${r.type === 'credit' ? fmt(r.amount || 0) : ''}</td>
      <td style="text-align:right;font-family:monospace;">${fmt(r.balance || 0)}</td>
      <td style="text-align:right;font-family:monospace;">${fmt(r.total_balance || 0)}</td>
    </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Cash Flow</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;font-size:11px;color:#1e293b;padding:20px;}h1{font-size:15px;font-weight:700;margin-bottom:4px;}.sub{font-size:11px;color:#64748b;margin-bottom:14px;}table{width:100%;border-collapse:collapse;}th{background:#f1f5f9;padding:7px 6px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;letter-spacing:.04em;}td{padding:5px 6px;border-bottom:1px solid #e2e8f0;vertical-align:top;}@media print{body{padding:8px;}}</style>
</head><body>
<h1>Cash Flow</h1><p class="sub">Period: ${fmtDate(startDate)} – ${fmtDate(endDate)} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()}</p>
<table><thead><tr><th>Date</th><th>Account</th><th>Description</th><th>Method</th><th style="text-align:right;">Debit</th><th style="text-align:right;">Credit</th><th style="text-align:right;">Acc. Balance</th><th style="text-align:right;">Total Balance</th></tr></thead>
<tbody>${rows}</tbody></table>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
</body></html>`;
    const w = window.open('', '_blank', 'width=1200,height=700');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ─── Print: Payment Report ───
  const printPaymentReport = (data, startDate, endDate) => {
    const fmt = (val) => {
      const num = parseFloat(val) || 0;
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const fmtDate = (d) => {
      if (!d) return '-';
      const match = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return match ? `${match[2]}/${match[3]}/${match[1]}` : d;
    };
    const rows = data.map(r => `<tr>
      <td>${fmtDate(r.paid_on)}</td>
      <td>${r.payment_ref_no || '-'}</td>
      <td>${r.invoice_no || r.ref_no || '-'}</td>
      <td style="text-align:right;font-family:monospace;">${fmt(r.amount || 0)}</td>
      <td>${r.type || '-'}</td>
      <td>${r.account_name ? r.account_name + (r.account_number ? ' - ' + r.account_number : '') : '-'}</td>
      <td>${(r.details || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}</td>
    </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Payment Account Report</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;font-size:11px;color:#1e293b;padding:20px;}h1{font-size:15px;font-weight:700;margin-bottom:4px;}.sub{font-size:11px;color:#64748b;margin-bottom:14px;}table{width:100%;border-collapse:collapse;}th{background:#f1f5f9;padding:7px 6px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;letter-spacing:.04em;}td{padding:5px 6px;border-bottom:1px solid #e2e8f0;vertical-align:top;}td:nth-child(4){text-align:right;}@media print{body{padding:8px;}}</style>
</head><body>
<h1>Payment Account Report</h1><p class="sub">Period: ${fmtDate(startDate)} – ${fmtDate(endDate)} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()}</p>
<table><thead><tr><th>Date</th><th>Payment Ref No.</th><th>Invoice No.</th><th style="text-align:right;">Amount</th><th>Payment Type</th><th>Account</th><th>Description</th></tr></thead>
<tbody>${rows}</tbody></table>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
</body></html>`;
    const w = window.open('', '_blank', 'width=1100,height=700');
    if (w) { w.document.write(html); w.document.close(); }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState('Active');
  const [openActionId, setOpenActionId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);

  // List Accounts pagination
  const [listAccountsPage, setListAccountsPage] = useState(1);
  const [listAccountsPerPage, setListAccountsPerPage] = useState(25);
  const [listAccountsPagination, setListAccountsPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  // Business locations for filters
  const [businessLocations, setBusinessLocations] = useState([]);

  // Balance Sheet state
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [balanceSheetDate, setBalanceSheetDate] = useState(new Date().toISOString().split('T')[0]);
  const [balanceSheetLocation, setBalanceSheetLocation] = useState('');
  const [balanceSheetLoading, setBalanceSheetLoading] = useState(false);

  // Trial Balance state
  const [trialBalanceData, setTrialBalanceData] = useState(null);
  const [trialBalanceDate, setTrialBalanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [trialBalanceLocation, setTrialBalanceLocation] = useState('');
  const [trialBalanceLoading, setTrialBalanceLoading] = useState(false);

  // Cash Flow state
  const [cashFlowData, setCashFlowData] = useState([]);
  const [cashFlowAccounts, setCashFlowAccounts] = useState([]);
  const [cashFlowStartDate, setCashFlowStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]);
  const [cashFlowEndDate, setCashFlowEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [cashFlowAccountFilter, setCashFlowAccountFilter] = useState('');
  const [cashFlowTypeFilter, setCashFlowTypeFilter] = useState('');
  const [cashFlowLocation, setCashFlowLocation] = useState('');
  const [cashFlowLoading, setCashFlowLoading] = useState(false);
  const [cashFlowPage, setCashFlowPage] = useState(1);
  const [cashFlowPerPage, setCashFlowPerPage] = useState(25);
  const [cashFlowShowExport, setCashFlowShowExport] = useState(false);

  // Payment Account Report state
  const [paymentReportData, setPaymentReportData] = useState([]);
  const [paymentReportAccounts, setPaymentReportAccounts] = useState([]);
  const todayStr = new Date().toISOString().split('T')[0];
  const currentYearStartStr = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];
  const [paymentReportStartDate, setPaymentReportStartDate] = useState(currentYearStartStr);
  const [paymentReportEndDate, setPaymentReportEndDate] = useState(todayStr);
  const [paymentReportAccountFilter, setPaymentReportAccountFilter] = useState('');
  const [paymentReportTypeFilter, setPaymentReportTypeFilter] = useState('');
  const [paymentReportLocation, setPaymentReportLocation] = useState('');
  const [paymentReportLoading, setPaymentReportLoading] = useState(false);
  const [paymentReportPage, setPaymentReportPage] = useState(1);
  const [paymentReportPerPage, setPaymentReportPerPage] = useState(25);
  const [paymentReportPagination, setPaymentReportPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [paymentReportSortColumn, setPaymentReportSortColumn] = useState('paid_on');
  const [paymentReportSortDirection, setPaymentReportSortDirection] = useState('desc');

  // Link Account modal state
  const [showLinkAccountModal, setShowLinkAccountModal] = useState(false);
  const [linkAccountPayment, setLinkAccountPayment] = useState(null);
  const [linkAccountId, setLinkAccountId] = useState('');
  const [linkAccountLoading, setLinkAccountLoading] = useState(false);

  // Account Book modal state
  const [showAccountBookModal, setShowAccountBookModal] = useState(false);
  const [accountBookData, setAccountBookData] = useState({ account: null, transactions: [] });
  const currentYearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];
  const today = new Date().toISOString().split('T')[0];
  const [accountBookStartDate, setAccountBookStartDate] = useState(currentYearStart);
  const [accountBookEndDate, setAccountBookEndDate] = useState(today);
  const [accountBookType, setAccountBookType] = useState('');
  const [accountBookPage, setAccountBookPage] = useState(1);
  const [accountBookPerPage, setAccountBookPerPage] = useState(25);
  const [accountBookPagination, setAccountBookPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [accountBookTotals, setAccountBookTotals] = useState({ debit: 0, credit: 0 });
  const [accountBookLoading, setAccountBookLoading] = useState(false);

  // Edit Account Transaction modal state
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
  const [editTransactionData, setEditTransactionData] = useState(null);
  const [editTransactionForm, setEditTransactionForm] = useState({
    amount: '',
    operation_date: '',
    note: '',
    account_id: '',
    from_account: '',
    to_account: ''
  });
  const [editTransactionLoading, setEditTransactionLoading] = useState(false);

  // Fund Transfer modal state
  const [showFundTransferModal, setShowFundTransferModal] = useState(false);
  const [fundTransferForm, setFundTransferForm] = useState({
    from_account: '',
    to_account: '',
    amount: '',
    note: '',
    operation_date: new Date().toISOString().split('T')[0]
  });

  // Deposit modal state
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositForm, setDepositForm] = useState({
    account_id: '',
    from_account: '',
    amount: '',
    note: '',
    operation_date: new Date().toISOString().split('T')[0]
  });

  const [accountForm, setAccountForm] = useState({
    name: '',
    accountNumber: '',
    accountType: '',
    openingBalance: '0',
    note: ''
  });
  const [accountDetails, setAccountDetails] = useState([{ label: '', value: '' }]);

  // Fetch accounts from API
  const fetchAccounts = async (page = 1, perPage = 25) => {
    setIsLoading(true);
    try {
      const res = await apiService.getPaymentAccounts({ page, per_page: perPage, account_status: filterStatus === 'All' ? 'all' : filterStatus === 'Closed' ? 'closed' : 'active' });
      const list = res?.data || res || [];
      setAccounts(list);
      if (res?.pagination) {
        setListAccountsPagination(res.pagination);
      }
    } catch (err) {
      /* console.error('Failed to fetch payment accounts:', err); */
      toast.error('Failed to load payment accounts');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch account types from API
  const fetchAccountTypes = async () => {
    try {
      const types = await apiService.getAccountTypes();
      setAccountTypes(types);
    } catch (err) {
      /* console.error('Failed to fetch account types:', err); */
    }
  };

  useEffect(() => {
    if (activeTab === 'list_accounts') {
      fetchAccounts(listAccountsPage, listAccountsPerPage);
    }
  }, [activeTab, listAccountsPage, listAccountsPerPage, filterStatus]);

  useEffect(() => {
    fetchAccountTypes();
  }, []);

  // Fetch business locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await apiService.getBusinessLocations();
        const list = res?.data || res || [];
        setBusinessLocations(Array.isArray(list) ? list : []);
      } catch (err) {
      /* console.error('Failed to fetch business locations:', err); */
      }
    };
    fetchLocations();
  }, []);

  // Fetch balance sheet when tab, date, or location changes
  const fetchBalanceSheet = async () => {
    setBalanceSheetLoading(true);
    try {
      const res = await apiService.getBalanceSheet({
        end_date: balanceSheetDate,
        location_id: balanceSheetLocation || undefined
      });
      setBalanceSheetData(res.data || res);
    } catch (err) {
      /* console.error('Failed to fetch balance sheet:', err); */
      toast.error('Failed to load balance sheet');
    } finally {
      setBalanceSheetLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'balance_sheet') {
      fetchBalanceSheet();
    }
  }, [activeTab, balanceSheetDate, balanceSheetLocation]);

  // Fetch trial balance when tab, date, or location changes
  const fetchTrialBalance = async () => {
    setTrialBalanceLoading(true);
    try {
      const res = await apiService.getTrialBalance({
        end_date: trialBalanceDate,
        location_id: trialBalanceLocation || undefined
      });
      setTrialBalanceData(res.data || res);
    } catch (err) {
      /* console.error('Failed to fetch trial balance:', err); */
      toast.error('Failed to load trial balance');
    } finally {
      setTrialBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'trial_balance') {
      fetchTrialBalance();
    }
  }, [activeTab, trialBalanceDate, trialBalanceLocation]);

  // Fetch cash flow when tab or filters change
  const fetchCashFlow = async () => {
    setCashFlowLoading(true);
    try {
      const res = await apiService.getCashFlow({
        start_date: cashFlowStartDate,
        end_date: cashFlowEndDate,
        account_id: cashFlowAccountFilter || undefined,
        type: cashFlowTypeFilter || undefined,
        location_id: cashFlowLocation || undefined
      });
      setCashFlowData(res.data || []);
      setCashFlowAccounts(res.accounts || []);
    } catch (err) {
      /* console.error('Failed to fetch cash flow:', err); */
      toast.error('Failed to load cash flow');
    } finally {
      setCashFlowLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'cash_flow') {
      fetchCashFlow();
    }
  }, [activeTab, cashFlowStartDate, cashFlowEndDate, cashFlowAccountFilter, cashFlowTypeFilter, cashFlowLocation]);

  // Fetch payment account report when tab or filters change
  const fetchPaymentAccountReport = async (page = paymentReportPage, perPage = paymentReportPerPage) => {
    setPaymentReportLoading(true);
    try {
      const res = await apiService.getPaymentAccountReport({
        start_date: paymentReportStartDate,
        end_date: paymentReportEndDate,
        account_id: paymentReportAccountFilter || undefined,
        type: paymentReportTypeFilter || undefined,
        location_id: paymentReportLocation || undefined,
        page,
        per_page: perPage,
        sort_column: paymentReportSortColumn,
        sort_direction: paymentReportSortDirection
      });
      setPaymentReportData(res.data || []);
      setPaymentReportAccounts(res.accounts || []);
      if (res.pagination) {
        setPaymentReportPagination(res.pagination);
      }
    } catch (err) {
      /* console.error('Failed to fetch payment account report:', err); */
      toast.error('Failed to load payment account report');
    } finally {
      setPaymentReportLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'payment_report') {
      fetchPaymentAccountReport();
    }
  }, [activeTab, paymentReportStartDate, paymentReportEndDate, paymentReportAccountFilter, paymentReportTypeFilter, paymentReportLocation, paymentReportPage, paymentReportPerPage, paymentReportSortColumn, paymentReportSortDirection]);

  const handleFormChange = (field, value) => {
    setAccountForm(prev => ({ ...prev, [field]: value }));
  };

  const addDetailRow = () => {
    setAccountDetails(prev => [...prev, { label: '', value: '' }]);
  };

  const removeDetailRow = (index) => {
    setAccountDetails(prev => prev.filter((_, i) => i !== index));
  };

  const handleDetailChange = (index, field, value) => {
    setAccountDetails(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  // Add/Edit Account Type Modal state
  const [showAddAccountTypeModal, setShowAddAccountTypeModal] = useState(false);
  const [editingAccountType, setEditingAccountType] = useState(null);
  const [accountTypeForm, setAccountTypeForm] = useState({
    name: '',
    parentAccountType: ''
  });

  const handleAccountTypeFormChange = (field, value) => {
    setAccountTypeForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddAccountTypeSubmit = async () => {
    const payload = {
      name: accountTypeForm.name,
      parent_account_type_id: accountTypeForm.parentAccountType || null
    };
    try {
      if (editingAccountType) {
        await apiService.updateAccountType(editingAccountType.id, payload);
        toast.success('Account type updated successfully');
      } else {
        await apiService.createAccountType(payload);
        toast.success('Account type created successfully');
      }
      setShowAddAccountTypeModal(false);
      setEditingAccountType(null);
      setAccountTypeForm({ name: '', parentAccountType: '' });
      fetchAccountTypes();
    } catch (err) {
      toast.error('Failed to save account type: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEditAccountType = (type) => {
    setEditingAccountType(type);
    setAccountTypeForm({
      name: type.name || '',
      parentAccountType: type.parent_account_type_id ? String(type.parent_account_type_id) : ''
    });
    setShowAddAccountTypeModal(true);
    setOpenActionId(null);
  };

  const handleDeleteAccountType = async (type) => {
    if (!window.confirm(`Are you sure you want to delete account type "${type.name}"?`)) return;
    try {
      await apiService.deleteAccountType(type.id);
      toast.success('Account type deleted successfully');
      fetchAccountTypes();
    } catch (err) {
      toast.error('Failed to delete account type: ' + (err.message || 'Unknown error'));
    }
    setOpenActionId(null);
  };

  const handleAddAccountSubmit = async () => {
    const payload = {
      name: accountForm.name,
      account_number: accountForm.accountNumber,
      account_type_id: accountForm.accountType,
      opening_balance: accountForm.openingBalance,
      note: accountForm.note,
      account_details: accountDetails.filter(d => d.label && d.value)
    };
    try {
      if (editingAccount) {
        await apiService.updatePaymentAccount(editingAccount.id, payload);
        toast.success('Account updated successfully');
      } else {
        await apiService.createPaymentAccount(payload);
        toast.success('Account created successfully');
      }
      setShowAddAccountModal(false);
      setEditingAccount(null);
      setAccountForm({ name: '', accountNumber: '', accountType: '', openingBalance: '0', note: '' });
      setAccountDetails([{ label: '', value: '' }]);
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to save account: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setAccountForm({
      name: account.name || '',
      accountNumber: account.account_number || '',
      accountType: account.account_type_id ? String(account.account_type_id) : '',
      openingBalance: '0',
      note: account.note || ''
    });
    setAccountDetails(account.account_details?.length ? account.account_details : [{ label: '', value: '' }]);
    setShowAddAccountModal(true);
    setOpenActionId(null);
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Are you sure you want to delete account "${account.name}"?`)) return;
    try {
      await apiService.deletePaymentAccount(account.id);
      toast.success('Account deleted successfully');
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to delete account: ' + (err.message || 'Unknown error'));
    }
    setOpenActionId(null);
  };

  const handleAccountBook = async (account, page = 1, perPage = 25) => {
    setOpenActionId(null);
    setAccountBookLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (accountBookStartDate) params.start_date = accountBookStartDate;
      if (accountBookEndDate) params.end_date = accountBookEndDate;
      if (accountBookType) params.type = accountBookType;
      const res = await apiService.getAccountBook(account.id, params);
      setAccountBookData({
        account: res.account || { name: account.name, id: account.id },
        transactions: res.data || []
      });
      if (res?.pagination) setAccountBookPagination(res.pagination);
      if (res?.totals) setAccountBookTotals(res.totals);
      setShowAccountBookModal(true);
    } catch (err) {
      toast.error('Failed to load account book: ' + (err.message || 'Unknown error'));
    } finally {
      setAccountBookLoading(false);
    }
  };

  const fetchAccountBookPage = async (page, perPage = accountBookPerPage) => {
    if (!accountBookData.account?.id) return;
    setAccountBookLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (accountBookStartDate) params.start_date = accountBookStartDate;
      if (accountBookEndDate) params.end_date = accountBookEndDate;
      if (accountBookType) params.type = accountBookType;
      const res = await apiService.getAccountBook(accountBookData.account.id, params);
      setAccountBookData(prev => ({
        ...prev,
        transactions: res.data || []
      }));
      if (res?.pagination) setAccountBookPagination(res.pagination);
      if (res?.totals) setAccountBookTotals(res.totals);
      setAccountBookPage(page);
    } catch (err) {
      toast.error('Failed to load account book: ' + (err.message || 'Unknown error'));
    } finally {
      setAccountBookLoading(false);
    }
  };

  const canDeleteTransaction = (t) => ['fund_transfer', 'deposit'].includes(t.sub_type);
  const canEditTransaction = (t) => ['fund_transfer', 'deposit', 'opening_balance'].includes(t.sub_type);

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    try {
      await apiService.deleteAccountTransaction(transactionId);
      toast.success('Transaction deleted successfully');
      if (accountBookData.account?.id) fetchAccountBookPage(accountBookPage);
    } catch (err) {
      toast.error('Failed to delete transaction: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEditTransaction = async (transactionId) => {
    setEditTransactionLoading(true);
    try {
      const res = await apiService.getAccountTransactionForEdit(transactionId);
      const data = res.data;
      setEditTransactionData(data);
      setEditTransactionForm({
        amount: data.amount || '',
        operation_date: data.date_iso || '',
        note: data.note || '',
        account_id: data.account_id || '',
        from_account: data.sub_type === 'deposit' ? (data.transfer_account_id || '') : '',
        to_account: data.sub_type === 'fund_transfer' ? (data.transfer_account_id || '') : ''
      });
      setShowEditTransactionModal(true);
    } catch (err) {
      toast.error('Failed to load transaction for edit: ' + (err.message || 'Unknown error'));
    } finally {
      setEditTransactionLoading(false);
    }
  };

  const handleUpdateTransaction = async () => {
    if (!editTransactionData?.id) return;
    setEditTransactionLoading(true);
    try {
      const payload = {
        amount: editTransactionForm.amount,
        operation_date: editTransactionForm.operation_date,
        note: editTransactionForm.note,
        account_id: editTransactionForm.account_id
      };
      if (editTransactionData.sub_type === 'deposit') {
        payload.from_account = editTransactionForm.from_account;
      }
      if (editTransactionData.sub_type === 'fund_transfer') {
        payload.to_account = editTransactionForm.to_account;
      }
      await apiService.updateAccountTransaction(editTransactionData.id, payload);
      toast.success('Transaction updated successfully');
      setShowEditTransactionModal(false);
      if (accountBookData.account?.id) fetchAccountBookPage(accountBookPage);
    } catch (err) {
      toast.error('Failed to update transaction: ' + (err.message || 'Unknown error'));
    } finally {
      setEditTransactionLoading(false);
    }
  };

  const handleFundTransfer = (account) => {
    setOpenActionId(null);
    setFundTransferForm({
      from_account: String(account.id),
      to_account: '',
      amount: '',
      note: '',
      operation_date: new Date().toISOString().split('T')[0]
    });
    setShowFundTransferModal(true);
  };

  const submitFundTransfer = async () => {
    try {
      await apiService.postFundTransfer(fundTransferForm);
      toast.success('Fund transferred successfully');
      setShowFundTransferModal(false);
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to transfer funds: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDeposit = (account) => {
    setOpenActionId(null);
    setDepositForm({
      account_id: String(account.id),
      from_account: '',
      amount: '',
      note: '',
      operation_date: new Date().toISOString().split('T')[0]
    });
    setShowDepositModal(true);
  };

  const submitDeposit = async () => {
    try {
      await apiService.postDeposit(depositForm);
      toast.success('Deposit successful');
      setShowDepositModal(false);
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to deposit: ' + (err.message || 'Unknown error'));
    }
  };

  const openLinkAccountModal = (row) => {
    setLinkAccountPayment(row);
    setLinkAccountId(row.account_id ? String(row.account_id) : '');
    setShowLinkAccountModal(true);
  };

  const handleLinkAccount = async () => {
    if (!linkAccountId) {
      toast.error('Please select an account');
      return;
    }
    setLinkAccountLoading(true);
    try {
      await apiService.linkPaymentAccount({
        transaction_payment_id: linkAccountPayment.id,
        account_id: linkAccountId
      });
      toast.success('Account linked successfully');
      setShowLinkAccountModal(false);
      setLinkAccountPayment(null);
      setLinkAccountId('');
      fetchPaymentAccountReport();
    } catch (err) {
      toast.error('Failed to link account: ' + (err.message || 'Unknown error'));
    } finally {
      setLinkAccountLoading(false);
    }
  };

  const handleCloseAccount = async (account) => {
    if (!window.confirm(`Are you sure you want to close account "${account.name}"?`)) return;
    try {
      await apiService.closeAccount(account.id);
      toast.success('Account closed successfully');
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to close account: ' + (err.message || 'Unknown error'));
    }
    setOpenActionId(null);
  };

  const toggleActionMenu = (id, event) => {
    event.stopPropagation();
    if (openActionId === id) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 200;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(id);
    }
  };

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const getAddButtonLabel = () => {
    switch (activeTab) {
      case 'list_accounts': return 'Add Account';
      case 'account_types': return 'Add Account Type';
      default: return null;
    }
  };

  return (
    <div className="products-container">

      {/* Tabs Row with Search + Filters */}
      <div className="main-card">


        {/* Filter Modal Popup */}
        {showFilters && (
          <div className="modal-overlay" onClick={() => setShowFilters(false)}>
            <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%' }}>
              <div className="modal-header">
                <h2>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                  </svg>
                  Filters
                </h2>
                <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
              </div>
              <div className="modal-content" style={{ padding: '1.5rem' }}>
                {activeTab === 'payment_report' && (
                  <div className="filter-row" style={{ flexDirection: 'column', gap: '1rem' }}>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Start Date:</label>
                      <input type="date" value={paymentReportStartDate} onChange={(e) => setPaymentReportStartDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>End Date:</label>
                      <input type="date" value={paymentReportEndDate} onChange={(e) => setPaymentReportEndDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Account:</label>
                      <select value={paymentReportAccountFilter} onChange={(e) => setPaymentReportAccountFilter(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                        <option value="">All Accounts</option>
                        {paymentReportAccounts.map(acc => (
                          <option key={acc.id} value={String(acc.id)}>{acc.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Type:</label>
                      <select value={paymentReportTypeFilter} onChange={(e) => setPaymentReportTypeFilter(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                        <option value="">All</option>
                        <option value="sell">Sale</option>
                        <option value="purchase">Purchase</option>
                        <option value="expense">Expense</option>
                        <option value="advance">Advance</option>
                      </select>
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Location:</label>
                      <select value={paymentReportLocation} onChange={(e) => setPaymentReportLocation(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                        <option value="">All Locations</option>
                        {businessLocations.map(loc => (
                          <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {activeTab === 'cash_flow' && (
                  <div className="filter-row" style={{ flexDirection: 'column', gap: '1rem' }}>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Start Date:</label>
                      <input type="date" value={cashFlowStartDate} onChange={(e) => setCashFlowStartDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>End Date:</label>
                      <input type="date" value={cashFlowEndDate} onChange={(e) => setCashFlowEndDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Account:</label>
                      <select value={cashFlowAccountFilter} onChange={(e) => setCashFlowAccountFilter(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                        <option value="">All Accounts</option>
                        {cashFlowAccounts.map(acc => (
                          <option key={acc.id} value={String(acc.id)}>{acc.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Type:</label>
                      <select value={cashFlowTypeFilter} onChange={(e) => setCashFlowTypeFilter(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                        <option value="">All</option>
                        <option value="credit">Credit</option>
                        <option value="debit">Debit</option>
                      </select>
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Location:</label>
                      <select value={cashFlowLocation} onChange={(e) => setCashFlowLocation(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                        <option value="">All Locations</option>
                        {businessLocations.map(loc => (
                          <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {activeTab === 'trial_balance' && (
                  <div className="filter-row" style={{ flexDirection: 'column', gap: '1rem' }}>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Date:</label>
                      <input type="date" value={trialBalanceDate} onChange={(e) => setTrialBalanceDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Location:</label>
                      <select value={trialBalanceLocation} onChange={(e) => setTrialBalanceLocation(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                        <option value="">All Locations</option>
                        {businessLocations.map(loc => (
                          <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {activeTab === 'balance_sheet' && (
                  <div className="filter-row" style={{ flexDirection: 'column', gap: '1rem' }}>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Date:</label>
                      <input type="date" value={balanceSheetDate} onChange={(e) => setBalanceSheetDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
                    </div>
                    <div className="filter-group" style={{ width: '100%' }}>
                      <label>Location:</label>
                      <select value={balanceSheetLocation} onChange={(e) => setBalanceSheetLocation(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                        <option value="">All Locations</option>
                        {businessLocations.map(loc => (
                          <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {activeTab === 'list_accounts' && (
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Account Status:</label>
                      <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="Active">Active</option>
                        <option value="Closed">Closed</option>
                        <option value="All">All</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>Account Type:</label>
                      <select>
                        <option value="all">All Types</option>
                        <option value="Bank">Bank</option>
                        <option value="Cash on Hand">Cash on Hand</option>
                        <option value="Equity">Equity</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Table Controls Row */}
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select
                value={activeTab === 'payment_report' ? paymentReportPerPage : activeTab === 'cash_flow' ? cashFlowPerPage : activeTab === 'list_accounts' ? listAccountsPerPage : 25}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (activeTab === 'payment_report') {
                    setPaymentReportPerPage(val);
                    setPaymentReportPage(1);
                  } else if (activeTab === 'cash_flow') {
                    setCashFlowPerPage(val);
                    setCashFlowPage(1);
                  } else if (activeTab === 'list_accounts') {
                    setListAccountsPerPage(val);
                    setListAccountsPage(1);
                  }
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right">
            <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                className="tabs-search-input"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            {/* Export Dropdown */}
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setShowExportMenu(!showExportMenu)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportMenu && (
                <div className="export-dropdown-menu">
                  {activeTab === 'cash_flow' && (
                    <>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Date', 'Account', 'Description', 'Payment Method', 'Payment Details', 'Debit', 'Credit', 'Account Balance', 'Total Balance'];
                        const rows = cashFlowData.map(r => [
                          formatDate(r.date) || '', r.account_name || '',
                          (r.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
                          r.method || '', (r.payment_details || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
                          r.type === 'debit' ? (r.amount || 0).toFixed(2) : '',
                          r.type === 'credit' ? (r.amount || 0).toFixed(2) : '',
                          (r.balance || 0).toFixed(2), (r.total_balance || 0).toFixed(2)
                        ]);
                        exportToCSV(headers, rows, 'cash_flow');
                      }}><i className="fa fa-file-csv"></i> Export CSV</button>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Date', 'Account', 'Description', 'Payment Method', 'Payment Details', 'Debit', 'Credit', 'Account Balance', 'Total Balance'];
                        const rows = cashFlowData.map(r => [
                          formatDate(r.date) || '', r.account_name || '',
                          (r.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
                          r.method || '', (r.payment_details || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
                          r.type === 'debit' ? (r.amount || 0).toFixed(2) : '',
                          r.type === 'credit' ? (r.amount || 0).toFixed(2) : '',
                          (r.balance || 0).toFixed(2), (r.total_balance || 0).toFixed(2)
                        ]);
                        exportToExcel(headers, rows, 'cash_flow');
                      }}><i className="fa fa-file-excel"></i> Export Excel</button>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Date', 'Account', 'Description', 'Payment Method', 'Payment Details', 'Debit', 'Credit', 'Account Balance', 'Total Balance'];
                        const rows = cashFlowData.map(r => [
                          formatDate(r.date) || '', r.account_name || '',
                          (r.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
                          r.method || '', (r.payment_details || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
                          r.type === 'debit' ? (r.amount || 0).toFixed(2) : '',
                          r.type === 'credit' ? (r.amount || 0).toFixed(2) : '',
                          (r.balance || 0).toFixed(2), (r.total_balance || 0).toFixed(2)
                        ]);
                        exportToPDF('Cash Flow', headers, rows, 'cash_flow');
                      }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                    </>
                  )}
                  {activeTab === 'list_accounts' && (
                    <>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Name', 'Account Type', 'Account Number', 'Note', 'Balance', 'Added By'];
                        const rows = accounts.map(a => [
                          a.name || '', a.account_type_name || '', a.account_number || '', a.note || '',
                          (a.balance || 0).toFixed(2), a.added_by || ''
                        ]);
                        exportToCSV(headers, rows, 'list_accounts');
                      }}><i className="fa fa-file-csv"></i> Export CSV</button>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Name', 'Account Type', 'Account Number', 'Note', 'Balance', 'Added By'];
                        const rows = accounts.map(a => [
                          a.name || '', a.account_type_name || '', a.account_number || '', a.note || '',
                          (a.balance || 0).toFixed(2), a.added_by || ''
                        ]);
                        exportToExcel(headers, rows, 'list_accounts');
                      }}><i className="fa fa-file-excel"></i> Export Excel</button>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Name', 'Account Type', 'Account Number', 'Note', 'Balance', 'Added By'];
                        const rows = accounts.map(a => [
                          a.name || '', a.account_type_name || '', a.account_number || '', a.note || '',
                          (a.balance || 0).toFixed(2), a.added_by || ''
                        ]);
                        exportToPDF('List Accounts', headers, rows, 'list_accounts');
                      }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                    </>
                  )}
                  {activeTab === 'payment_report' && (
                    <>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Date', 'Payment Ref No.', 'Invoice No.', 'Amount', 'Payment Type', 'Account', 'Description'];
                        const rows = paymentReportData.map(r => [
                          formatDate(r.paid_on) || '', r.payment_ref_no || '', r.invoice_no || r.ref_no || '',
                          (r.amount || 0).toFixed(2), r.type || '', r.account_name ? `${r.account_name} - ${r.account_number || ''}` : '',
                          (r.details || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
                        ]);
                        exportToCSV(headers, rows, 'payment_report');
                      }}><i className="fa fa-file-csv"></i> Export CSV</button>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Date', 'Payment Ref No.', 'Invoice No.', 'Amount', 'Payment Type', 'Account', 'Description'];
                        const rows = paymentReportData.map(r => [
                          formatDate(r.paid_on) || '', r.payment_ref_no || '', r.invoice_no || r.ref_no || '',
                          (r.amount || 0).toFixed(2), r.type || '', r.account_name ? `${r.account_name} - ${r.account_number || ''}` : '',
                          (r.details || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
                        ]);
                        exportToExcel(headers, rows, 'payment_report');
                      }}><i className="fa fa-file-excel"></i> Export Excel</button>
                      <button onClick={() => {
                        setShowExportMenu(false);
                        const headers = ['Date', 'Payment Ref No.', 'Invoice No.', 'Amount', 'Payment Type', 'Account', 'Description'];
                        const rows = paymentReportData.map(r => [
                          formatDate(r.paid_on) || '', r.payment_ref_no || '', r.invoice_no || r.ref_no || '',
                          (r.amount || 0).toFixed(2), r.type || '', r.account_name ? `${r.account_name} - ${r.account_number || ''}` : '',
                          (r.details || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
                        ]);
                        exportToPDF('Payment Account Report', headers, rows, 'payment_report');
                      }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                    </>
                  )}
                  {/* Print - each tab opens a dedicated clean print window */}
                  <button onClick={() => {
                    setShowExportMenu(false);
                    if (activeTab === 'balance_sheet') {
                      printBalanceSheet(balanceSheetData, balanceSheetDate);
                    } else if (activeTab === 'list_accounts') {
                      printListAccounts(accounts);
                    } else if (activeTab === 'account_types') {
                      printAccountTypes(accountTypes);
                    } else if (activeTab === 'trial_balance') {
                      printTrialBalance(trialBalanceData, trialBalanceDate);
                    } else if (activeTab === 'cash_flow') {
                      printCashFlow(cashFlowData, cashFlowStartDate, cashFlowEndDate);
                    } else if (activeTab === 'payment_report') {
                      printPaymentReport(paymentReportData, paymentReportStartDate, paymentReportEndDate);
                    } else {
                      printPage();
                    }
                  }}><i className="fa fa-print"></i> Print</button>
                </div>
              )}
            </div>

            {/* Add Button */}
            {getAddButtonLabel() && (
              <button className="control-btn-primary" onClick={() => {
                if (activeTab === 'list_accounts') {
                  setEditingAccount(null);
                  setAccountForm({ name: '', accountNumber: '', accountType: '', openingBalance: '0', note: '' });
                  setAccountDetails([{ label: '', value: '' }]);
                  setShowAddAccountModal(true);
                }
                if (activeTab === 'account_types') {
                  setEditingAccountType(null);
                  setAccountTypeForm({ name: '', parentAccountType: '' });
                  setShowAddAccountTypeModal(true);
                }
              }}>
                <span className="plus-icon">+</span> {getAddButtonLabel()}
              </button>
            )}
          </div>
        </div>

        {/* List Accounts Table */}
        {activeTab === 'list_accounts' && (
          <div className="products-table-container">
            {isLoading ? (
              <div className="loading-sales" style={{ padding: '2rem', textAlign: 'center' }}>
                <h3>Loading accounts...</h3>
              </div>
            ) : (
              <>
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Name</th>
                      <th>Account Type</th>
                      <th>Account Sub Type</th>
                      <th>Account Number</th>
                      <th>Note</th>
                      <th>Balance</th>
                      <th>Account details</th>
                      <th>Added By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts
                      .filter(a =>
                        (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (a.account_number || '').toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((account) => {
                        return (
                          <tr key={account.id}>
                            <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                              <div className="action-dropdown-container">
                                <button
                                  className="action-btn-trigger"
                                  onClick={(e) => { e.stopPropagation(); toggleActionMenu(account.id, e); }}
                                >
                                  Actions <span className="arrow-down">▼</span>
                                </button>
                                {openActionId === account.id && ReactDOM.createPortal(
                                  <div
                                    className="action-dropdown-menu"
                                    style={{
                                      position: 'fixed',
                                      top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                      bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                      left: `${dropdownPosition.left}px`,
                                      maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                      zIndex: 9999999
                                    }}
                                  >
                                    <button onClick={() => handleEdit(account)}>✏️ Edit</button>
                                    <button onClick={() => handleAccountBook(account)}>📖 Account Book</button>
                                    <button onClick={() => handleFundTransfer(account)}>💸 Fund Transfer</button>
                                    <button onClick={() => handleDeposit(account)}>💰 Deposit</button>
                                    <button onClick={() => handleCloseAccount(account)}>🔒 Close</button>
                                    <button className="delete-btn" onClick={() => handleDelete(account)}>🗑️ Delete</button>
                                  </div>,
                                  document.body
                                )}
                              </div>
                            </td>
                            <td>
                              {account.name}
                              {account.is_closed == 1 && (
                                <span style={{ display: 'inline-block', marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', backgroundColor: '#dc2626', color: 'white', fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Closed</span>
                              )}
                            </td>
                            <td>{account.parent_account_type_name || account.account_type_name || '-'}</td>
                            <td>{account.account_type_name || '-'}</td>
                            <td>{account.account_number || '-'}</td>
                            <td>{account.note || '-'}</td>
                            <td style={{ color: (account.balance || 0) >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                              {formatCurrency(account.balance || 0)}
                            </td>
                            <td>{(account.account_details || []).filter(d => d && (d.label || d.value)).map(d => `${d.label || ''}: ${d.value || ''}`).join(', ') || '-'}</td>
                            <td>{account.added_by || '-'}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>

                {/* Pagination bottom */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white', marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--etpos-text-muted)' }}>
                    Showing {listAccountsPagination.from || 1} to {listAccountsPagination.to || accounts.length} of {listAccountsPagination.total || accounts.length} entries
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="pagination-btn" onClick={() => setListAccountsPage(Math.max(1, listAccountsPage - 1))} disabled={listAccountsPage === 1}>Previous</button>
                    <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.85rem' }}>Page {listAccountsPage} of {Math.max(1, listAccountsPagination.last_page)}</span>
                    <button className="pagination-btn" onClick={() => setListAccountsPage(Math.min(listAccountsPagination.last_page, listAccountsPage + 1))} disabled={listAccountsPage >= listAccountsPagination.last_page}>Next</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Account Types Table */}
        {activeTab === 'account_types' && (
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Account Type</th>
                  <th>Parent Account Type</th>
                </tr>
              </thead>
              <tbody>
                {accountTypes
                  .filter(t => (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((type) => (
                    <tr key={type.id}>
                      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        <div className="action-dropdown-container">
                          <button
                            className="action-btn-trigger"
                            onClick={(e) => { e.stopPropagation(); toggleActionMenu(`type-${type.id}`, e); }}
                          >
                            Actions <span className="arrow-down">▼</span>
                          </button>
                          {openActionId === `type-${type.id}` && ReactDOM.createPortal(
                            <div
                              className="action-dropdown-menu"
                              style={{
                                position: 'fixed',
                                top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                left: `${dropdownPosition.left}px`,
                                maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                zIndex: 9999999
                              }}
                            >
                              <button onClick={() => handleEditAccountType(type)}>✏️ Edit</button>
                              <button className="delete-btn" onClick={() => handleDeleteAccountType(type)}>🗑️ Delete</button>
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td>{type.name}</td>
                      <td>{type.parent_name || '-'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Balance Sheet */}
        {activeTab === 'balance_sheet' && (
          <div className="bs-print-wrapper" style={{ background: 'var(--etpos-card, #fff)', borderRadius: '0.75rem', border: '1px solid var(--etpos-border)' }}>
            {balanceSheetLoading ? (
              <div className="loading-sales" style={{ padding: '2rem', textAlign: 'center' }}>
                <h3>Loading balance sheet...</h3>
              </div>
            ) : !balanceSheetData ? (
              <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>No data available.</p>
            ) : (
              <div style={{ padding: '1.5rem' }}>
                {/* Balance Sheet Grid */}
                <div className="bs-print-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, border: '1px solid var(--etpos-border)', borderRadius: '0.5rem' }}>
                  {/* Liabilities Column */}
                  <div className="bs-print-col" style={{ borderRight: '1px solid var(--etpos-border)' }}>
                    <div style={{ background: 'var(--etpos-bg, #f8fafc)', padding: '0.75rem 1rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--etpos-border)' }}>Liability</div>
                    <div style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px dashed var(--etpos-border)' }}>
                        <span style={{ fontWeight: 500 }}>Supplier Due</span>
                        <span style={{ fontFamily: 'monospace' }}>{formatCurrency(balanceSheetData.supplier_due || 0)}</span>
                      </div>
                    </div>
                    <div style={{ background: 'var(--etpos-bg, #f8fafc)', padding: '0.75rem 1rem', fontWeight: 700, fontSize: '0.9rem', borderTop: '2px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total Liability</span>
                      <span style={{ fontFamily: 'monospace' }}>{formatCurrency(balanceSheetData.supplier_due || 0)}</span>
                    </div>
                  </div>

                  {/* Assets Column */}
                  <div className="bs-print-col">
                    <div style={{ background: 'var(--etpos-bg, #f8fafc)', padding: '0.75rem 1rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--etpos-border)' }}>Assets</div>
                    <div style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px dashed var(--etpos-border)' }}>
                        <span style={{ fontWeight: 500 }}>Customer Due</span>
                        <span style={{ fontFamily: 'monospace' }}>{formatCurrency(balanceSheetData.customer_due || 0)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px dashed var(--etpos-border)' }}>
                        <span style={{ fontWeight: 500 }}>Closing Stock</span>
                        <span style={{ fontFamily: 'monospace' }}>{formatCurrency(balanceSheetData.closing_stock || 0)}</span>
                      </div>
                      <div style={{ padding: '0.75rem 0 0.25rem', fontWeight: 600, fontSize: '0.85rem', color: 'var(--etpos-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Account Balances</div>
                      {Object.entries(balanceSheetData.account_balances || {}).map(([name, bal]) => (
                        <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', paddingLeft: '1rem' }}>
                          <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>{name}</span>
                          <span style={{ fontFamily: 'monospace' }}>{formatCurrency(bal || 0)}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ background: 'var(--etpos-bg, #f8fafc)', padding: '0.75rem 1rem', fontWeight: 700, fontSize: '0.9rem', borderTop: '2px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total Assets</span>
                      <span style={{ fontFamily: 'monospace' }}>{formatCurrency((balanceSheetData.customer_due || 0) + (balanceSheetData.closing_stock || 0) + Object.values(balanceSheetData.account_balances || {}).reduce((a, b) => a + parseFloat(b || 0), 0))}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment Account Report */}
        {activeTab === 'payment_report' && (
          <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '0.75rem', border: '1px solid var(--etpos-border)', overflow: 'hidden' }}>
            {paymentReportLoading ? (
              <div className="loading-sales" style={{ padding: '2rem', textAlign: 'center' }}>
                <h3>Loading payment account report...</h3>
              </div>
            ) : paymentReportData.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>No records found.</p>
            ) : (
              <div style={{ padding: '0.75rem' }}>
                <table className="products-table" style={{ fontSize: '0.8rem', width: '100%', minWidth: '100%', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ background: 'var(--etpos-bg, #f8fafc)' }}>
                      {[
                        { key: 'paid_on', label: 'Date', width: '12%', align: 'left' },
                        { key: 'payment_ref_no', label: 'Payment Ref No.', width: '12%', align: 'left' },
                        { key: 'invoice_no', label: 'Invoice No./Ref. No.', width: '14%', align: 'left' },
                        { key: 'amount', label: 'Amount', width: '10%', align: 'right' },
                        { key: 'type', label: 'Payment Type', width: '10%', align: 'left' },
                        { key: 'account_name', label: 'Account', width: '14%', align: 'left' },
                        { key: 'contact_name', label: 'Description', width: '14%', align: 'left' },
                      ].map(col => (
                        <th
                          key={col.key}
                          onClick={() => {
                            if (paymentReportSortColumn === col.key) {
                              setPaymentReportSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setPaymentReportSortColumn(col.key);
                              setPaymentReportSortDirection('asc');
                            }
                            setPaymentReportPage(1);
                          }}
                          style={{ padding: '0.5rem 0.75rem', textAlign: col.align, width: col.width, cursor: 'pointer', userSelect: 'none' }}
                          title={`Sort by ${col.label}`}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                            {col.label}
                            <span style={{ fontSize: '0.7rem', color: paymentReportSortColumn === col.key ? 'var(--etpos-primary)' : '#94a3b8' }}>
                              {paymentReportSortColumn === col.key ? (paymentReportSortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                            </span>
                          </span>
                        </th>
                      ))}
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', width: '14%' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentReportData.map((row) => (
                      <tr key={row.id}>
                        <td style={{ padding: '0.4rem 0.75rem', wordBreak: 'break-word' }}>{formatDate(row.paid_on)}</td>
                        <td style={{ padding: '0.4rem 0.75rem', wordBreak: 'break-word' }}>{row.payment_ref_no || '-'}</td>
                        <td style={{ padding: '0.4rem 0.75rem', wordBreak: 'break-word' }}>
                          {row.type === 'sell' && row.invoice_no ? (
                            <button type="button" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', border: '1px solid var(--etpos-primary)', borderRadius: '4px', color: 'var(--etpos-primary)', background: 'none', cursor: 'pointer' }}>{row.invoice_no}</button>
                          ) : row.type === 'purchase' && row.ref_no ? (
                            <button type="button" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', border: '1px solid var(--etpos-primary)', borderRadius: '4px', color: 'var(--etpos-primary)', background: 'none', cursor: 'pointer' }}>{row.ref_no}</button>
                          ) : (
                            row.invoice_no || row.ref_no || '-'
                          )}
                        </td>
                        <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.amount || 0)}</td>
                        <td style={{ padding: '0.4rem 0.75rem', wordBreak: 'break-word' }}>{row.type || '-'}</td>
                        <td style={{ padding: '0.4rem 0.75rem', wordBreak: 'break-word' }}>{row.account_id ? `${row.account_name} - ${row.account_number || ''}` : '-'}</td>
                        <td style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', wordBreak: 'break-word' }}>
                          <div dangerouslySetInnerHTML={{ __html: row.details || '-' }} />
                        </td>
                        <td style={{ padding: '0.4rem 0.75rem', textAlign: 'center' }}>
                          <button
                            onClick={() => openLinkAccountModal(row)}
                            style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', border: '1px solid var(--etpos-primary)', borderRadius: '4px', color: 'var(--etpos-primary)', background: 'none', cursor: 'pointer' }}
                          >
                            Link Account
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination bottom */}
                {paymentReportData.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white', marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--etpos-text-muted)' }}>
                      Showing {paymentReportPagination.from || 0} to {paymentReportPagination.to || 0} of {paymentReportPagination.total || 0} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="pagination-btn" onClick={() => setPaymentReportPage(p => Math.max(1, p - 1))} disabled={paymentReportPagination.current_page <= 1}>Previous</button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.85rem' }}>Page {paymentReportPagination.current_page || 1} of {paymentReportPagination.last_page || 1}</span>
                      <button className="pagination-btn" onClick={() => setPaymentReportPage(p => Math.min(paymentReportPagination.last_page || 1, p + 1))} disabled={paymentReportPagination.current_page >= (paymentReportPagination.last_page || 1)}>Next</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Link Account Modal */}
            {showLinkAccountModal && (
              <div className="modal-overlay" style={{ zIndex: 99999999 }}>
                <div className="modal-container" style={{ maxWidth: '450px', width: '100%' }}>
                  <div className="modal-header">
                    <h3>Link Account - Payment Ref No.: - {linkAccountPayment?.payment_ref_no || ''}</h3>
                    <button className="close-btn" onClick={() => { setShowLinkAccountModal(false); setLinkAccountPayment(null); setLinkAccountId(''); }}>&times;</button>
                  </div>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Account:</label>
                      <select
                        value={linkAccountId}
                        onChange={(e) => setLinkAccountId(e.target.value)}
                        style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)', width: '100%' }}
                        required
                      >
                        <option value="">Select Account</option>
                        {paymentReportAccounts.map(acc => (
                          <option key={acc.id} value={String(acc.id)}>{acc.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="cancel-btn" onClick={() => { setShowLinkAccountModal(false); setLinkAccountPayment(null); setLinkAccountId(''); }}>Close</button>
                    <button className="submit-btn" onClick={handleLinkAccount} disabled={linkAccountLoading}>
                      {linkAccountLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cash Flow */}
        {activeTab === 'cash_flow' && (
          <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '0.75rem', border: '1px solid var(--etpos-border)' }}>
            {cashFlowLoading ? (
              <div className="loading-sales" style={{ padding: '2rem', textAlign: 'center' }}>
                <h3>Loading cash flow...</h3>
              </div>
            ) : cashFlowData.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>No transactions found.</p>
            ) : (
              <div style={{ padding: '0.75rem' }}>
                <div>
                  <table className="products-table" style={{ fontSize: '0.75rem', width: '100%', minWidth: '100%', tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ background: 'var(--etpos-bg, #f8fafc)' }}>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', width: '11%' }}>Date</th>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', width: '9%' }}>Account</th>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', width: '20%' }}>Description</th>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', width: '9%' }}>Payment Method</th>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', width: '13%' }}>Payment Details</th>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right', width: '8%' }}>Debit</th>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right', width: '8%' }}>Credit</th>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right', width: '11%' }}>Account Balance</th>
                        <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right', width: '11%' }}>Total Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const start = (cashFlowPage - 1) * cashFlowPerPage;
                        const end = start + cashFlowPerPage;
                        const pageData = cashFlowData.slice(start, end);
                        const lastRow = pageData[pageData.length - 1];
                        const pageDebit = pageData.filter(r => r.type === 'debit').reduce((a, r) => a + (r.amount || 0), 0);
                        const pageCredit = pageData.filter(r => r.type === 'credit').reduce((a, r) => a + (r.amount || 0), 0);
                        return (
                          <>
                            {pageData.map((row) => (
                              <tr key={row.id}>
                                <td style={{ padding: '0.3rem 0.4rem', wordBreak: 'break-word' }}>{formatDate(row.date)}</td>
                                <td style={{ padding: '0.3rem 0.4rem', wordBreak: 'break-word' }}>{row.account_name}</td>
                                <td style={{ padding: '0.3rem 0.4rem', fontSize: '0.7rem', wordBreak: 'break-word' }}>
                                  <div dangerouslySetInnerHTML={{ __html: row.description || '-' }} />
                                </td>
                                <td style={{ padding: '0.3rem 0.4rem', wordBreak: 'break-word' }}>{row.method || '-'}</td>
                                <td style={{ padding: '0.3rem 0.4rem', fontSize: '0.7rem', wordBreak: 'break-word' }}>
                                  <div dangerouslySetInnerHTML={{ __html: row.payment_details || '-' }} />
                                </td>
                                <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', fontFamily: 'monospace' }}>{row.type === 'debit' ? formatCurrency(row.amount || 0) : '-'}</td>
                                <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', fontFamily: 'monospace' }}>{row.type === 'credit' ? formatCurrency(row.amount || 0) : '-'}</td>
                                <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatCurrency(row.balance || 0)}</td>
                                <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatCurrency(row.total_balance || 0)}</td>
                              </tr>
                            ))}
                            {/* Total Row */}
                            <tr style={{ background: 'var(--etpos-bg, #f8fafc)', fontWeight: 700 }}>
                              <td colSpan={5} style={{ padding: '0.35rem 0.4rem', textAlign: 'right', borderTop: '2px solid var(--etpos-border)' }}>Total</td>
                              <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontFamily: 'monospace', borderTop: '2px solid var(--etpos-border)' }}>
                                {formatCurrency(pageDebit)}
                              </td>
                              <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontFamily: 'monospace', borderTop: '2px solid var(--etpos-border)' }}>
                                {formatCurrency(pageCredit)}
                              </td>
                              <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontFamily: 'monospace', borderTop: '2px solid var(--etpos-border)' }}>&nbsp;</td>
                              <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontFamily: 'monospace', borderTop: '2px solid var(--etpos-border)' }}>
                                {formatCurrency(lastRow?.total_balance || 0)}
                              </td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Pagination bottom */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white', marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--etpos-text-muted)' }}>
                    Showing {((cashFlowPage - 1) * cashFlowPerPage) + 1} to {Math.min(cashFlowPage * cashFlowPerPage, cashFlowData.length)} of {cashFlowData.length} entries
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="pagination-btn" onClick={() => setCashFlowPage(Math.max(1, cashFlowPage - 1))} disabled={cashFlowPage === 1}>Previous</button>
                    <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.85rem' }}>Page {cashFlowPage} of {Math.max(1, Math.ceil(cashFlowData.length / cashFlowPerPage))}</span>
                    <button className="pagination-btn" onClick={() => setCashFlowPage(Math.min(Math.max(1, Math.ceil(cashFlowData.length / cashFlowPerPage)), cashFlowPage + 1))} disabled={cashFlowPage >= Math.ceil(cashFlowData.length / cashFlowPerPage)}>Next</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Trial Balance */}
        {activeTab === 'trial_balance' && (
          <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '0.75rem', border: '1px solid var(--etpos-border)', overflow: 'hidden' }}>
            {trialBalanceLoading ? (
              <div className="loading-sales" style={{ padding: '2rem', textAlign: 'center' }}>
                <h3>Loading trial balance...</h3>
              </div>
            ) : !trialBalanceData ? (
              <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>No data available.</p>
            ) : (
              <div style={{ padding: '1.5rem' }}>
                {/* Trial Balance Table */}
                <table className="products-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%' }}>
                  <thead>
                    <tr style={{ background: 'var(--etpos-bg, #f8fafc)' }}>
                      <th style={{ textAlign: 'left', padding: '0.75rem 1rem', borderBottom: '2px solid var(--etpos-border)', width: '45%' }}>Trial Balance</th>
                      <th style={{ textAlign: 'right', padding: '0.75rem 1rem', borderBottom: '2px solid var(--etpos-border)', width: '27.5%' }}>Debit</th>
                      <th style={{ textAlign: 'right', padding: '0.75rem 1rem', borderBottom: '2px solid var(--etpos-border)', width: '27.5%' }}>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Supplier Due - Credit side only */}
                    <tr>
                      <td style={{ padding: '0.5rem 1rem', fontWeight: 600 }}>Supplier Due:</td>
                      <td style={{ padding: '0.5rem 1rem' }}>&nbsp;</td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(trialBalanceData.supplier_due || 0)}</td>
                    </tr>
                    {/* Customer Due - Debit side only */}
                    <tr>
                      <td style={{ padding: '0.5rem 1rem', fontWeight: 600 }}>Customer Due:</td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(trialBalanceData.customer_due || 0)}</td>
                      <td style={{ padding: '0.5rem 1rem' }}>&nbsp;</td>
                    </tr>
                    {/* Account Balances header */}
                    <tr>
                      <td style={{ padding: '0.5rem 1rem', fontWeight: 600 }}>Account Balances:</td>
                      <td style={{ padding: '0.5rem 1rem' }}>&nbsp;</td>
                      <td style={{ padding: '0.5rem 1rem' }}>&nbsp;</td>
                    </tr>
                    {/* Account Balances - Debit side */}
                    {Object.entries(trialBalanceData.account_balances || {}).map(([name, bal]) => (
                      <tr key={name}>
                        <td style={{ padding: '0.35rem 1rem', paddingLeft: '2rem' }}>{name}:</td>
                        <td style={{ padding: '0.35rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(bal || 0)}</td>
                        <td style={{ padding: '0.35rem 1rem' }}>&nbsp;</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--etpos-bg, #f8fafc)', fontWeight: 700 }}>
                      <td style={{ padding: '0.75rem 1rem', borderTop: '2px solid var(--etpos-border)' }}>Total</td>
                      <td style={{ padding: '0.75rem 1rem', borderTop: '2px solid var(--etpos-border)', textAlign: 'right', fontFamily: 'monospace' }}>
                        {(() => {
                          const debitTotal = (trialBalanceData.customer_due || 0) + Object.values(trialBalanceData.account_balances || {}).reduce((a, b) => a + parseFloat(b || 0), 0);
                          return formatCurrency(debitTotal);
                        })()}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderTop: '2px solid var(--etpos-border)', textAlign: 'right', fontFamily: 'monospace' }}>
                        {formatCurrency(trialBalanceData.supplier_due || 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Account Modal */}
      {showAddAccountModal && (
        <div className="modal-overlay" onClick={() => { setShowAddAccountModal(false); setEditingAccount(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
              <h3 style={{ margin: 0 }}>{editingAccount ? 'Edit Account' : 'Add Account'}</h3>
              <button className="close-btn" onClick={() => { setShowAddAccountModal(false); setEditingAccount(null); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              {/* Name */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Name: <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  placeholder="Name"
                  value={accountForm.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                />
              </div>

              {/* Account Number */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Account Number: <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  placeholder="Account Number"
                  value={accountForm.accountNumber}
                  onChange={(e) => handleFormChange('accountNumber', e.target.value)}
                />
              </div>

              {/* Account Type */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Account Type:</label>
                <select
                  value={accountForm.accountType}
                  onChange={(e) => handleFormChange('accountType', e.target.value)}
                >
                  <option value="">Select Account Type</option>
                  {accountTypes.map(t => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}
                </select>
              </div>

              {/* Opening Balance */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Opening Balance:</label>
                <input
                  type="number"
                  placeholder="0"
                  value={accountForm.openingBalance}
                  onChange={(e) => handleFormChange('openingBalance', e.target.value)}
                />
              </div>

              {/* Account Details - Label/Value rows */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: '500', fontSize: '0.875rem', color: 'var(--etpos-text)', display: 'block', marginBottom: '0.5rem' }}>Account details:</label>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--etpos-border)', borderRadius: '0.5rem' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '0.5rem 0.75rem', background: 'var(--etpos-bg)', fontSize: '0.8rem', fontWeight: '600', textAlign: 'left', borderBottom: '1px solid var(--etpos-border)' }}>Label</th>
                      <th style={{ padding: '0.5rem 0.75rem', background: 'var(--etpos-bg)', fontSize: '0.8rem', fontWeight: '600', textAlign: 'left', borderBottom: '1px solid var(--etpos-border)' }}>Value</th>
                      <th style={{ padding: '0.5rem', background: 'var(--etpos-bg)', borderBottom: '1px solid var(--etpos-border)', width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountDetails.map((row, index) => (
                      <tr key={index}>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <input
                            type="text"
                            placeholder="Label"
                            value={row.label}
                            onChange={(e) => handleDetailChange(index, 'label', e.target.value)}
                            style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--etpos-border)', borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <input
                            type="text"
                            placeholder="Value"
                            value={row.value}
                            onChange={(e) => handleDetailChange(index, 'value', e.target.value)}
                            style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--etpos-border)', borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.4rem 0.25rem', textAlign: 'center' }}>
                          {accountDetails.length > 1 && (
                            <button
                              onClick={() => removeDetailRow(index)}
                              style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', borderRadius: '4px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  onClick={addDetailRow}
                  style={{ marginTop: '0.5rem', padding: '0.35rem 0.75rem', background: 'var(--etpos-primary)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer' }}
                >
                  + Add Row
                </button>
              </div>

              {/* Note */}
              <div className="form-group" style={{ marginBottom: '0' }}>
                <label>Note:</label>
                <textarea
                  placeholder="Note"
                  value={accountForm.note}
                  onChange={(e) => handleFormChange('note', e.target.value)}
                  rows={3}
                  style={{ padding: '0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '0.5rem', fontSize: '0.95rem', resize: 'vertical', width: '100%', fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="cancel-btn" onClick={() => setShowAddAccountModal(false)}>Cancel</button>
              <button className="submit-btn" onClick={handleAddAccountSubmit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Account Book Modal */}
      {showAccountBookModal && (
        <div className="modal-overlay" onClick={() => setShowAccountBookModal(false)} style={{ alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '98vw', maxWidth: '1400px', height: '95vh', maxHeight: '95vh', display: 'flex', flexDirection: 'column', borderRadius: '12px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700' }}>Account Book</h3>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', opacity: 0.9 }}>{accountBookData.account?.name} — {accountBookData.account?.account_type || 'Account'}</p>
              </div>
              <button className="close-btn" onClick={() => setShowAccountBookModal(false)} style={{ color: 'white', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>

            {/* Account Info + Filters Bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.5rem', background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid #e2e8f0', flexShrink: 0, flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <div><span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Account Number</span><div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b' }}>{accountBookData.account?.account_number || '—'}</div></div>
                <div><span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Account Type</span><div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b' }}>{accountBookData.account?.account_type || '—'}</div></div>
                <div><span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Status</span><div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{accountBookData.account?.is_closed ? <span style={{ color: '#ef4444' }}>Closed</span> : <span style={{ color: '#10b981' }}>Active</span>}</div></div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From</label>
                  <input type="date" value={accountBookStartDate} onChange={(e) => setAccountBookStartDate(e.target.value)} style={{ padding: '0.45rem 0.6rem', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', minWidth: '135px' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>To</label>
                  <input type="date" value={accountBookEndDate} onChange={(e) => setAccountBookEndDate(e.target.value)} style={{ padding: '0.45rem 0.6rem', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', minWidth: '135px' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</label>
                  <select value={accountBookType} onChange={(e) => setAccountBookType(e.target.value)} style={{ padding: '0.45rem 0.6rem', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', minWidth: '120px', background: 'var(--etpos-card, white)' }}>
                    <option value="">All Transactions</option>
                    <option value="credit">Credit</option>
                    <option value="debit">Debit</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Show</label>
                  <select value={accountBookPerPage} onChange={(e) => { const val = Number(e.target.value); setAccountBookPerPage(val); setAccountBookPage(1); if (accountBookData.account?.id) fetchAccountBookPage(1, val); }} style={{ padding: '0.45rem 0.6rem', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', minWidth: '80px', background: 'var(--etpos-card, white)' }}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <button
                  onClick={() => { setAccountBookPage(1); if (accountBookData.account?.id) fetchAccountBookPage(1); }}
                  style={{ padding: '0.5rem 1.25rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', boxShadow: '0 2px 4px rgba(37,99,235,0.2)' }}
                >
                  Apply Filters
                </button>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Current Balance</span>
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: (accountBookData.account?.balance || 0) >= 0 ? '#059669' : '#dc2626' }}>
                  {formatCurrency(accountBookData.account?.balance || 0)}
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div style={{ padding: '0', overflow: 'auto', flex: 1, background: 'var(--etpos-bg, #f1f5f9)' }}>
              {accountBookLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <div style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1rem' }}></div>
                  <p style={{ color: '#64748b', fontWeight: '500' }}>Loading transactions...</p>
                </div>
              ) : accountBookData.transactions.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="9" x2="15" y2="9"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>
                  <p style={{ color: '#64748b', marginTop: '1rem', fontSize: '1rem' }}>No transactions found.</p>
                </div>
              ) : (
                <div style={{ padding: '0.75rem 1.5rem' }}>
                  <table style={{ fontSize: '0.82rem', width: '100%', borderCollapse: 'separate', borderSpacing: '0', background: 'var(--etpos-card, white)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                    <thead>
                      <tr style={{ background: '#1e293b', color: 'white' }}>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'left', fontWeight: '600', whiteSpace: 'nowrap', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'left', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'left', fontWeight: '600', whiteSpace: 'nowrap', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Method</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'left', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Details</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'left', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Note</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'left', fontWeight: '600', whiteSpace: 'nowrap', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Added By</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Debit</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Credit</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Balance</th>
                        <th style={{ padding: '0.7rem 0.8rem', textAlign: 'center', fontWeight: '600', whiteSpace: 'nowrap', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountBookData.transactions.map((t, idx) => (
                        <tr key={t.id} style={{ background: idx % 2 === 0 ? 'var(--etpos-card, white)' : 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'} onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? 'var(--etpos-card, white)' : 'var(--etpos-bg, #f8fafc)'}>
                          <td style={{ padding: '0.65rem 0.8rem', whiteSpace: 'nowrap', color: 'var(--etpos-text-muted, #475569)', fontWeight: '500' }}>{t.formatted_date || '-'}</td>
                          <td style={{ padding: '0.65rem 0.8rem', maxWidth: '280px', color: '#334155', lineHeight: '1.4' }} dangerouslySetInnerHTML={{ __html: (t.description || '-').replace(/\\n/g, '<br/>') }}></td>
                          <td style={{ padding: '0.65rem 0.8rem', whiteSpace: 'nowrap', color: 'var(--etpos-text-muted, #475569)' }}>{t.method || '-'}</td>
                          <td style={{ padding: '0.65rem 0.8rem', maxWidth: '200px', color: 'var(--etpos-text-muted, #475569)', lineHeight: '1.4' }} dangerouslySetInnerHTML={{ __html: (t.payment_details || '-').replace(/\\n/g, '<br/>') }}></td>
                          <td style={{ padding: '0.65rem 0.8rem', color: '#64748b', fontStyle: t.note ? 'normal' : 'italic' }}>{t.note || '—'}</td>
                          <td style={{ padding: '0.65rem 0.8rem', whiteSpace: 'nowrap', color: 'var(--etpos-text-muted, #475569)' }}>{t.added_by || '-'}</td>
                          <td style={{ padding: '0.65rem 0.8rem', textAlign: 'right', color: '#dc2626', fontWeight: '700', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{t.debit > 0 ? formatCurrency(t.debit) : ''}</td>
                          <td style={{ padding: '0.65rem 0.8rem', textAlign: 'right', color: '#059669', fontWeight: '700', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{t.credit > 0 ? formatCurrency(t.credit) : ''}</td>
                          <td style={{ padding: '0.65rem 0.8rem', textAlign: 'right', fontWeight: '700', whiteSpace: 'nowrap', color: '#1e293b', fontFamily: 'monospace' }}>{formatCurrency(t.balance || 0)}</td>
                          <td style={{ padding: '0.65rem 0.8rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {(canEditTransaction(t) || canDeleteTransaction(t)) && (
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                {canDeleteTransaction(t) && (
                                  <button onClick={() => handleDeleteTransaction(t.id)} style={{ padding: '4px 10px', fontSize: '0.7rem', borderRadius: '5px', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: '600' }} title="Delete">Delete</button>
                                )}
                                {canEditTransaction(t) && (
                                  <button onClick={() => handleEditTransaction(t.id)} style={{ padding: '4px 10px', fontSize: '0.7rem', borderRadius: '5px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: '600' }} title="Edit">Edit</button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#e2e8f0', fontWeight: '800', borderTop: '2px solid #cbd5e1' }}>
                        <td colSpan={6} style={{ textAlign: 'right', padding: '0.8rem', fontSize: '0.85rem', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</td>
                        <td style={{ textAlign: 'right', color: '#dc2626', padding: '0.8rem', fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: '800' }}>{formatCurrency(accountBookTotals.debit || 0)}</td>
                        <td style={{ textAlign: 'right', color: '#059669', padding: '0.8rem', fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: '800' }}>{formatCurrency(accountBookTotals.credit || 0)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Pagination bottom */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', marginTop: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>
                      Showing {accountBookPagination.from || 1} to {accountBookPagination.to || accountBookData.transactions.length} of {accountBookPagination.total || accountBookData.transactions.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <button onClick={() => fetchAccountBookPage(Math.max(1, accountBookPage - 1))} disabled={accountBookPage === 1} style={{ padding: '0.4rem 1rem', fontSize: '0.8rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'var(--etpos-card, white)', color: 'var(--etpos-text-muted, #475569)', cursor: accountBookPage === 1 ? 'not-allowed' : 'pointer', opacity: accountBookPage === 1 ? 0.5 : 1, fontWeight: '600' }}>Previous</button>
                      <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '600', padding: '0 0.5rem' }}>Page {accountBookPage} of {Math.max(1, accountBookPagination.last_page)}</span>
                      <button onClick={() => fetchAccountBookPage(Math.min(accountBookPagination.last_page, accountBookPage + 1))} disabled={accountBookPage >= accountBookPagination.last_page} style={{ padding: '0.4rem 1rem', fontSize: '0.8rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'var(--etpos-card, white)', color: 'var(--etpos-text-muted, #475569)', cursor: accountBookPage >= accountBookPagination.last_page ? 'not-allowed' : 'pointer', opacity: accountBookPage >= accountBookPagination.last_page ? 0.5 : 1, fontWeight: '600' }}>Next</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      {showEditTransactionModal && editTransactionData && (
        <div className="modal-overlay" onClick={() => setShowEditTransactionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
              <h3 style={{ margin: 0 }}>
                {editTransactionData.sub_type === 'opening_balance' ? 'Edit Opening Balance' :
                  editTransactionData.sub_type === 'fund_transfer' ? 'Edit Fund Transfer' :
                    editTransactionData.sub_type === 'deposit' ? 'Edit Deposit' : 'Edit Transaction'}
              </h3>
              <button className="close-btn" onClick={() => setShowEditTransactionModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--etpos-bg, #f8fafc)', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <strong>Selected Account:</strong> {editTransactionData.account_name}
              </div>

              {/* Account select for deposit/fund_transfer */}
              {(editTransactionData.sub_type === 'deposit' || editTransactionData.sub_type === 'fund_transfer') && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>
                    {editTransactionData.sub_type === 'deposit'
                      ? (editTransactionData.type === 'debit' ? 'Deposit From' : 'Deposit To')
                      : (editTransactionData.type === 'credit' ? 'Transfer To' : 'Transfer From')}
                    : <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select value={editTransactionForm.account_id} onChange={(e) => setEditTransactionForm(prev => ({ ...prev, account_id: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                    <option value="">Select Account</option>
                    {Object.entries(editTransactionData.accounts || {}).map(([id, name]) => (
                      <option key={id} value={String(id)}>{name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Amount: <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="number" step="0.01" value={editTransactionForm.amount} onChange={(e) => setEditTransactionForm(prev => ({ ...prev, amount: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
              </div>

              {/* Transfer account for deposit */}
              {editTransactionData.sub_type === 'deposit' && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>{editTransactionData.type === 'debit' ? 'Deposit To' : 'Deposit From'}:</label>
                  <select value={editTransactionForm.from_account} onChange={(e) => setEditTransactionForm(prev => ({ ...prev, from_account: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                    <option value="">Select Account</option>
                    {Object.entries(editTransactionData.accounts || {}).map(([id, name]) => (
                      <option key={id} value={String(id)}>{name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Transfer account for fund_transfer */}
              {editTransactionData.sub_type === 'fund_transfer' && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>{editTransactionData.type === 'credit' ? 'Transfer From' : 'Transfer To'}: <span style={{ color: '#ef4444' }}>*</span></label>
                  <select value={editTransactionForm.to_account} onChange={(e) => setEditTransactionForm(prev => ({ ...prev, to_account: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }}>
                    <option value="">Select Account</option>
                    {Object.entries(editTransactionData.accounts || {}).map(([id, name]) => (
                      <option key={id} value={String(id)}>{name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Date: <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="date" value={editTransactionForm.operation_date} onChange={(e) => setEditTransactionForm(prev => ({ ...prev, operation_date: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
              </div>

              {(editTransactionData.sub_type === 'fund_transfer' || editTransactionData.sub_type === 'deposit') && (
                <div className="form-group" style={{ marginBottom: '0' }}>
                  <label>Note:</label>
                  <textarea value={editTransactionForm.note} onChange={(e) => setEditTransactionForm(prev => ({ ...prev, note: e.target.value }))} rows={3} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)', resize: 'vertical' }} />
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center', padding: '1rem 1.5rem' }}>
              <button className="cancel-btn" onClick={() => setShowEditTransactionModal(false)}>Cancel</button>
              <button className="submit-btn" onClick={handleUpdateTransaction} disabled={editTransactionLoading}>
                {editTransactionLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fund Transfer Modal */}
      {showFundTransferModal && (
        <div className="modal-overlay" onClick={() => setShowFundTransferModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
              <h3 style={{ margin: 0 }}>Fund Transfer</h3>
              <button className="close-btn" onClick={() => setShowFundTransferModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>From Account:</label>
                <input type="text" value={accounts.find(a => String(a.id) === fundTransferForm.from_account)?.name || ''} disabled style={{ background: '#f1f5f9' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>To Account: <span style={{ color: '#ef4444' }}>*</span></label>
                <select value={fundTransferForm.to_account} onChange={(e) => setFundTransferForm(prev => ({ ...prev, to_account: e.target.value }))}>
                  <option value="">Select Account</option>
                  {accounts.filter(a => String(a.id) !== fundTransferForm.from_account).map(a => (
                    <option key={a.id} value={String(a.id)}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Amount: <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="number" value={fundTransferForm.amount} onChange={(e) => setFundTransferForm(prev => ({ ...prev, amount: e.target.value }))} placeholder="0.00" />
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Note:</label>
                <textarea value={fundTransferForm.note} onChange={(e) => setFundTransferForm(prev => ({ ...prev, note: e.target.value }))} rows={2} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="cancel-btn" onClick={() => setShowFundTransferModal(false)}>Cancel</button>
              <button className="submit-btn" onClick={submitFundTransfer}>Transfer</button>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="modal-overlay" onClick={() => setShowDepositModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
              <h3 style={{ margin: 0 }}>Deposit</h3>
              <button className="close-btn" onClick={() => setShowDepositModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>To Account:</label>
                <input type="text" value={accounts.find(a => String(a.id) === depositForm.account_id)?.name || ''} disabled style={{ background: '#f1f5f9' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>From Account:</label>
                <select value={depositForm.from_account} onChange={(e) => setDepositForm(prev => ({ ...prev, from_account: e.target.value }))}>
                  <option value="">Select Account (Optional)</option>
                  {accounts.filter(a => String(a.id) !== depositForm.account_id).map(a => (
                    <option key={a.id} value={String(a.id)}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Amount: <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="number" value={depositForm.amount} onChange={(e) => setDepositForm(prev => ({ ...prev, amount: e.target.value }))} placeholder="0.00" />
              </div>
              <div className="form-group" style={{ marginBottom: '0' }}>
                <label>Note:</label>
                <textarea value={depositForm.note} onChange={(e) => setDepositForm(prev => ({ ...prev, note: e.target.value }))} rows={2} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--etpos-border)' }} />
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="cancel-btn" onClick={() => setShowDepositModal(false)}>Cancel</button>
              <button className="submit-btn" onClick={submitDeposit}>Deposit</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Account Type Modal */}
      {showAddAccountTypeModal && (
        <div className="modal-overlay" onClick={() => setShowAddAccountTypeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
              <h3 style={{ margin: 0 }}>{editingAccountType ? 'Edit Account Type' : 'Add Account Type'}</h3>
              <button className="close-btn" onClick={() => setShowAddAccountTypeModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              {/* Name */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Name: <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  placeholder="Name"
                  value={accountTypeForm.name}
                  onChange={(e) => handleAccountTypeFormChange('name', e.target.value)}
                />
              </div>

              {/* Parent Account Type */}
              <div className="form-group" style={{ marginBottom: '0' }}>
                <label>Parent account type:</label>
                <select
                  value={accountTypeForm.parentAccountType}
                  onChange={(e) => handleAccountTypeFormChange('parentAccountType', e.target.value)}
                >
                  <option value="">Please Select</option>
                  {accountTypes.filter(t => !t.parent_account_type_id).map(t => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}

                </select>
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="cancel-btn" onClick={() => setShowAddAccountTypeModal(false)}>Cancel</button>
              <button className="submit-btn" onClick={handleAddAccountTypeSubmit}>Save</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PaymentAccounts;
